const QUILL_RENDER_AS = 'quill';
const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;
const ALLOWED_TAGS = new Set([
    'P',
    'BR',
    'STRONG',
    'B',
    'EM',
    'I',
    'U',
    'S',
    'STRIKE',
    'UL',
    'OL',
    'LI',
    'A',
    'SPAN',
    'SUB',
    'SUP',
]);
const COMMENT_SELECTORS = [
    '.sd-comment',
    '.sv_q_comment',
    '.sd-input--comment',
    'textarea',
    '.sv-string-viewer',
];
function isQuillQuestion(question) {
    const renderAs = readRenderAs(question);
    if (renderAs === QUILL_RENDER_AS)
        return true;
    const type = typeof question.getType === 'function' ? question.getType() : '';
    if (type !== 'comment')
        return false;
    const value = question.value;
    return typeof value === 'string' && (looksLikeHtml(value) || looksLikeEscapedHtml(value));
}
function readRenderAs(question) {
    const direct = question.renderAs;
    if (direct != null && String(direct).trim()) {
        return String(direct).toLowerCase();
    }
    if (typeof question.toJSON === 'function') {
        const json = question.toJSON();
        if (json?.renderAs != null)
            return String(json.renderAs).toLowerCase();
    }
    return '';
}
function looksLikeHtml(value) {
    return HTML_TAG_RE.test(value);
}
function looksLikeEscapedHtml(value) {
    return /&lt;\/?[a-z]/i.test(value);
}
function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
function maybeUnescape(value) {
    if (looksLikeHtml(value) || !looksLikeEscapedHtml(value))
        return value;
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
}
function toDisplayHtml(value) {
    const raw = value == null ? '' : String(value);
    const trimmed = maybeUnescape(raw.trim());
    if (!trimmed)
        return '';
    const source = looksLikeHtml(trimmed)
        ? trimmed
        : trimmed
            .split(/\n{2,}/)
            .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
            .join('');
    const sanitized = sanitizeQuillHtml(source).replace(/<p><br><\/p>/gi, '').trim();
    if (!sanitized || sanitized === '<p></p>')
        return '';
    return sanitized;
}
function isSafeHref(href) {
    return /^(https?:|mailto:|#)/i.test(href.trim());
}
function sanitizeStyle(style) {
    const allowed = [];
    for (const part of style.split(';')) {
        const [rawProp, ...rest] = part.split(':');
        const prop = rawProp?.trim().toLowerCase();
        const value = rest.join(':').trim();
        if (!prop || !value)
            continue;
        if (prop !== 'color' && prop !== 'background-color')
            continue;
        if (!/^(#[0-9a-f]{3,8}|rgba?\([^)]+\))$/i.test(value))
            continue;
        allowed.push(`${prop}: ${value}`);
    }
    return allowed.join('; ');
}
function stripAttributes(el) {
    const keep = [];
    if (el.tagName === 'A') {
        const href = el.getAttribute('href')?.trim() ?? '';
        if (href && isSafeHref(href))
            keep.push({ name: 'href', value: href });
        const target = el.getAttribute('target')?.trim() ?? '';
        if (target === '_blank') {
            keep.push({ name: 'target', value: '_blank' });
            keep.push({ name: 'rel', value: 'noopener noreferrer' });
        }
    }
    if (el.tagName === 'SPAN') {
        const style = sanitizeStyle(el.getAttribute('style') ?? '');
        if (style)
            keep.push({ name: 'style', value: style });
    }
    for (const attr of [...el.attributes]) {
        el.removeAttribute(attr.name);
    }
    for (const attr of keep) {
        el.setAttribute(attr.name, attr.value);
    }
}
function sanitizeQuillHtml(html) {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root)
        return '';
    const walk = (parent) => {
        for (const child of [...parent.childNodes]) {
            if (child.nodeType === Node.COMMENT_NODE) {
                parent.removeChild(child);
                continue;
            }
            if (child.nodeType !== Node.ELEMENT_NODE)
                continue;
            const el = child;
            if (!ALLOWED_TAGS.has(el.tagName)) {
                while (el.firstChild)
                    parent.insertBefore(el.firstChild, el);
                parent.removeChild(el);
                walk(parent);
                return;
            }
            stripAttributes(el);
            walk(el);
        }
    };
    walk(root);
    return root.innerHTML;
}
function hideNode(el) {
    if (el.classList.contains('quill-html-display'))
        return;
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
}
function renderQuillHtml(question, htmlElement) {
    const html = toDisplayHtml(question.value);
    const host = htmlElement.querySelector('.sd-question__content') ??
        htmlElement.querySelector('.sd-element__content') ??
        htmlElement;
    let viewer = host.querySelector(':scope > .quill-html-display');
    if (!viewer) {
        viewer = document.createElement('div');
        viewer.className = 'quill-html-display';
        host.appendChild(viewer);
    }
    viewer.innerHTML = html || '<p class="quill-html-empty">—</p>';
    const originals = findCommentDisplays(host);
    if (originals.length > 0) {
        originals.forEach(hideNode);
        return;
    }
    const snippet = String(question.value ?? '')
        .replace(/<[^>]+>/g, '')
        .slice(0, 40);
    for (const child of [...host.children]) {
        if (child === viewer || !(child instanceof HTMLElement))
            continue;
        if (snippet && child.textContent?.includes(snippet))
            hideNode(child);
    }
}
function findCommentDisplays(host) {
    const found = [];
    for (const selector of COMMENT_SELECTORS) {
        found.push(...[...host.querySelectorAll(selector)].filter((el) => !el.classList.contains('quill-html-display')));
    }
    if (found.length > 0)
        return found;
    const fallback = host.querySelector('.sd-input');
    return fallback && !fallback.classList.contains('quill-html-display') ? [fallback] : [];
}
function findQuestionRoot(root, question) {
    const id = question.id;
    if (id) {
        try {
            const byId = root.querySelector(`#${CSS.escape(id)}`);
            if (byId)
                return byId;
        }
        catch {
            const byId = document.getElementById(id);
            if (byId && root.contains(byId))
                return byId;
        }
    }
    const name = question.name;
    if (!name)
        return null;
    const matches = [...root.querySelectorAll(`[data-name="${CSS.escape(name)}"]`)];
    if (matches.length <= 1)
        return matches[0] ?? null;
    const snippet = String(question.value ?? '').replace(/<[^>]+>/g, '').slice(0, 40);
    return (matches.find((el) => snippet && el.textContent?.includes(snippet)) ??
        matches.find((el) => el.querySelector('textarea, .sd-comment')) ??
        matches[0]);
}
function applyToQuestion(question, htmlElement) {
    if (!htmlElement || !isQuillQuestion(question))
        return;
    renderQuillHtml(question, htmlElement);
}
/** Walk a rendered survey root and replace quill comment values with HTML. */
export function applyQuillHtmlDisplay(survey, root) {
    const questions = typeof survey.getAllQuestions === 'function'
        ? survey.getAllQuestions(false, false, true)
        : [];
    for (const question of questions) {
        applyToQuestion(question, findQuestionRoot(root, question));
    }
}
/** Attach before <Survey> mounts so the first display render is converted. */
export function bindQuillHtmlDisplay(survey) {
    const onQuestion = (_sender, options) => {
        applyToQuestion(options.question, options.htmlElement);
    };
    const onSurvey = (_sender, options) => {
        const root = options.htmlElement;
        if (root)
            applyQuillHtmlDisplay(survey, root);
    };
    survey.onAfterRenderQuestion.add(onQuestion);
    survey.onAfterRenderQuestionInput.add(onQuestion);
    survey.onAfterRenderSurvey.add(onSurvey);
    return () => {
        survey.onAfterRenderQuestion.remove(onQuestion);
        survey.onAfterRenderQuestionInput.remove(onQuestion);
        survey.onAfterRenderSurvey.remove(onSurvey);
    };
}
